import { useFragment, useQuery } from '@apollo/client';
import classNames from 'classnames';
import * as _ from 'lodash-es';
import type { Op } from 'quill/core';
import Quill from 'quill/core';
import React, { useEffect, useState } from 'react';
import type { DistributedOmit } from 'type-fest';
import { useEffectEvent } from 'use-effect-event';
import * as styles from './CopilotMessageComposer.module.scss';
import type { InputSources } from '../../ai/useAiCopilot';
import Button from '../../components/Button';
import ToggleControl from '../../components/formInternals/ToggleControl';
import Menu, { MenuItem } from '../../components/Menu';
import PopOverPrimitive from '../../components/PopOverPrimitive';
import SVGIcon from '../../components/SVGIcon';
import { MOD_KEY } from '../../constants/os';
import { gql } from '../../graphql/generated';
import allPostsIcon from '../../icons/non-prefilled/16x16/allPosts.svg';
import internetIcon from '../../icons/non-prefilled/16x16/internet.svg';
import postIcon from '../../icons/non-prefilled/16x16/post.svg';
import addTestId from '../../utils/addTestId';
import getPostIdsFromOps from '../../utils/getPostIdsFromOps';
import useSlug from '../../utils/router/useSlug';
import type { Binding } from '../BaseKeyboard';
import PostMentionPicker from '../controls/PostMentionPicker';
import LinkOnlyEditorToolbar from '../controls/toolbar/LinkOnlyEditorToolbar';
import QuillComponent from '../QuillComponent';
import { useQuill } from '../QuillContext';
import deltaToText from '../utils/deltaToText';

const SelectableMenuItem = ({
  selected,
  ...props
}: DistributedOmit<
  React.ComponentProps<typeof MenuItem>,
  'right' | 'submenuItems'
> & {
  selected?: boolean;
}) => {
  return (
    <MenuItem
      right={
        <div className={styles.toggleContainer}>
          <ToggleControl
            type="checkbox"
            checked={selected}
            readOnly
            config={{ knobSize: 16, trackWidth: 24 }}
          />
        </div>
      }
      {...props}
    />
  );
};

interface PostTitleProps {
  children: string;
}

const PostTitle = ({ children }: PostTitleProps) => {
  return (
    <div className={styles.postTitleContainer}>
      <div>&#8220;</div>
      <div className={styles.postTitle}>{children}</div>
      <div>&#8221;</div>
    </div>
  );
};

interface InputSourceIconProps {
  inputSources: InputSources;
  postOption?: PostOption;
}

const InputSourceIcon = ({
  inputSources,
  postOption,
}: InputSourceIconProps) => {
  const isSlabEnabled = inputSources.slab.enabled;
  const isWebEnabled = inputSources.web.enabled;
  const isPostEnabled =
    postOption &&
    (inputSources.currentPost.enabled || inputSources.mentionedPosts.enabled);

  if (!isSlabEnabled && !isWebEnabled && !isPostEnabled) {
    return <SVGIcon size={16}>{allPostsIcon}</SVGIcon>;
  }

  return (
    <>
      {isSlabEnabled && <SVGIcon size={16}>{allPostsIcon}</SVGIcon>}
      {isWebEnabled && <SVGIcon size={16}>{internetIcon}</SVGIcon>}
      {isPostEnabled && <SVGIcon size={16}>{postIcon}</SVGIcon>}
    </>
  );
};

type SinglePostOptionContent = {
  type: 'single';
  title: string;
};

type MultiplePostOptionContent = {
  type: 'multiple';
  count: number;
};

type PostOption =
  | {
      type: 'mentionedPosts';
      content: SinglePostOptionContent | MultiplePostOptionContent;
    }
  | {
      type: 'currentPost';
      content: SinglePostOptionContent;
    };

interface InputSourcesMenuProps {
  children: React.ReactNode;
  inputSources: InputSources;
  onToggleInputSource: (source: keyof InputSources) => void;
  postOption?: PostOption;
}

const InputSourcesMenu = ({
  children,
  inputSources,
  onToggleInputSource,
  postOption,
}: InputSourcesMenuProps) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <PopOverPrimitive
      trigger="click"
      open={isOpen}
      onOpenChange={setIsOpen}
      placement="top-start"
      offset={6}
    >
      <PopOverPrimitive.Trigger>{children}</PopOverPrimitive.Trigger>
      <PopOverPrimitive.Floating>
        <PopOverPrimitive.Content
          className={styles.inputSourcesMenuContent}
          {...addTestId('inputSourcesMenu')}
        >
          <Menu>
            {postOption && (
              <SelectableMenuItem
                selected={inputSources[postOption.type].enabled}
                onClick={() => {
                  onToggleInputSource(postOption.type);
                }}
                icon={postIcon}
              >
                {postOption.content.type === 'single' ? (
                  <PostTitle>{postOption.content.title}</PostTitle>
                ) : (
                  <>Mentioned posts ({postOption.content.count})</>
                )}
              </SelectableMenuItem>
            )}
            <SelectableMenuItem
              selected={inputSources.slab.enabled}
              onClick={() => {
                onToggleInputSource('slab');
              }}
              icon={allPostsIcon}
            >
              Search open posts
            </SelectableMenuItem>
            <SelectableMenuItem
              selected={inputSources.web.enabled}
              onClick={() => {
                onToggleInputSource('web');
              }}
              icon={internetIcon}
            >
              Search internet
            </SelectableMenuItem>
          </Menu>
        </PopOverPrimitive.Content>
      </PopOverPrimitive.Floating>
    </PopOverPrimitive>
  );
};

export const getMessageTextFromOps = (ops: Op[]) =>
  deltaToText(ops.filter((op) => typeof op.insert === 'string')).trim();

const MODULE_CONFIG = {
  prefixListener: true,
  clipboard: {
    allowedMentionTypes: new Set(['post']),
  },
};

const useOnEnter = (callback: () => void) => {
  const { quill } = useQuill();

  const onEnterEvent = useEffectEvent(() => {
    callback();
  });

  useEffect(() => {
    if (!quill) return undefined;

    const binding: Binding = {
      key: 'Enter',
      shiftKey: false,
      [MOD_KEY]: null,
      handler() {
        onEnterEvent();
      },
    };

    // @ts-expect-error
    quill.keyboard.prependBinding(binding);

    return () => {
      // @ts-expect-error
      quill.keyboard.removeBinding(binding);
    };
    // We need to make sure that the binding is mounted only once, so the deps
    // should only include quill and nothing else. The reason is because we are
    // prepending the binding, and in PostMentionPicker we are relying on PrefixTriggeredMenu,
    // which also relies on prepending the binding, so the order is important.
  }, [quill]);
};

const POST_FRAGMENT = gql(`
  fragment CopilotMessageComposer_post on Post {
    id
    title
  }
`);

const POST_QUERY = gql(/** @complexity 1 */ `
  query CopilotMessageComposerPost($id: ID!) {
    post(id: $id) {
      id
      ...CopilotMessageComposer_post
    }
  }
`);

const usePostOption = (
  mentionedPostIds: string[],
  currentPostId: string | undefined,
): PostOption | undefined => {
  const [postId] = [...mentionedPostIds, currentPostId];
  const postFragment = useFragment({
    fragment: POST_FRAGMENT,
    fragmentName: 'CopilotMessageComposer_post',
    from: postId ? { id: postId, __typename: 'Post' } : null,
  });

  const postQuery = useQuery(POST_QUERY, {
    variables: { id: postId ?? '' },
    skip: postFragment.complete || !postId,
  });

  const post = postFragment.complete ? postFragment.data : postQuery.data?.post;

  if (mentionedPostIds.length > 1) {
    return {
      type: 'mentionedPosts',
      content: {
        type: 'multiple',
        count: mentionedPostIds.length,
      },
    };
  }

  if (post) {
    return {
      type: mentionedPostIds.length ? 'mentionedPosts' : 'currentPost',
      content: {
        type: 'single',
        title: post.title,
      },
    };
  }

  return undefined;
};

interface CopilotMessageComposerProps {
  isGenerating: boolean;
  onSend: () => void;
  onStop: () => void;
  inputSources: InputSources;
  setInputSources: React.Dispatch<React.SetStateAction<InputSources>>;
  editControl?: {
    onCancel: () => void;
    onEdit: () => void;
  };
}

const CopilotMessageComposer = ({
  isGenerating,
  onSend,
  onStop,
  inputSources,
  setInputSources,
  editControl,
}: CopilotMessageComposerProps) => {
  const [container, setContainer] = useState<HTMLDivElement | null>(null);
  const { quill } = useQuill();
  const [hasInputContent, setHasInputContent] = useState(false);
  const [currentPostId] = useSlug('postSlug');

  const postOption = usePostOption(
    inputSources.mentionedPosts.postIds,
    currentPostId,
  );

  const handleTextChangeEvent = useEffectEvent(() => {
    if (!quill) return;
    const { ops } = quill.getContents();
    const text = getMessageTextFromOps(ops);
    setHasInputContent(text !== '');
    setInputSources((prev) => {
      const postIds = getPostIdsFromOps(ops);

      if (_.isEqual(prev.mentionedPosts.postIds, postIds)) {
        return prev;
      }

      return {
        ...prev,
        currentPost: {
          enabled: false,
        },
        mentionedPosts: {
          postIds,
          enabled: postIds.length > 0,
        },
      };
    });
  });

  useEffect(() => {
    if (!quill) return undefined;

    quill.on(Quill.events.TEXT_CHANGE, handleTextChangeEvent);

    return () => {
      quill.off(Quill.events.TEXT_CHANGE, handleTextChangeEvent);
    };
  }, [quill]);

  useOnEnter(() => {
    if (isGenerating || !hasInputContent) return;
    if (editControl) {
      editControl.onEdit();
      return;
    }
    onSend();
  });

  return (
    <div
      ref={setContainer}
      className={classNames(styles.messageComposer, 'copilot-message-composer')}
    >
      <div className={styles.inputContainer}>
        {quill && container && (
          <LinkOnlyEditorToolbar quill={quill} portal={container} />
        )}
        <QuillComponent
          noTitleLine
          namespace="copliot"
          contentPlaceholder="Ask about your Slab posts"
          readOnly={false}
          autoFocus
          moduleConfig={MODULE_CONFIG}
        />
        {quill && (
          <PostMentionPicker
            quill={quill}
            postId={currentPostId}
            excludePostIds={new Set(inputSources.mentionedPosts.postIds)}
          />
        )}
      </div>
      <div className={styles.actions}>
        <div className={styles.actionsLeft}>
          <InputSourcesMenu
            postOption={postOption}
            inputSources={inputSources}
            onToggleInputSource={(source) => {
              setInputSources((prev) => {
                return {
                  ...prev,
                  currentPost:
                    source === 'mentionedPosts'
                      ? {
                          enabled: false,
                        }
                      : prev.currentPost,
                  [source]: {
                    ...prev[source],
                    enabled: !prev[source].enabled,
                  },
                };
              });
            }}
          >
            <Button className={styles.iconButton}>
              <InputSourceIcon
                inputSources={inputSources}
                postOption={postOption}
              />
              <span>Sources</span>
            </Button>
          </InputSourcesMenu>
        </div>
        <div className={styles.actionsRight}>
          {editControl ? (
            <>
              <Button
                size="small"
                variant="secondary"
                onClick={editControl.onCancel}
              >
                Cancel
              </Button>
              <Button
                size="small"
                variant="primary"
                disabled={!hasInputContent}
                onClick={editControl.onEdit}
              >
                Edit
              </Button>
            </>
          ) : (
            <>
              {isGenerating ? (
                <Button
                  size="small"
                  variant="primary"
                  className={styles.stopButton}
                  onClick={onStop}
                >
                  Stop
                </Button>
              ) : (
                <Button
                  size="small"
                  variant="primary"
                  disabled={!hasInputContent}
                  onClick={onSend}
                >
                  Send
                </Button>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default CopilotMessageComposer;
